import { Box, CloseButton, Icon, IconButton, Slide } from "@chakra-ui/react";
import { WorkingOnLabel } from "./WorkingOnLabel";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FiChevronsLeft, FiChevronsRight } from "react-icons/fi";
import { useLocation } from "react-router-dom";
import { urls } from "../../urls";
import { useLoggedInWithOrgContextState } from "../auth/useLoggedInWithOrgContextState";
import { Tooltip } from "../components/Tooltip";
import { AssistantChat } from "./AssistantChat";
import { useAssistantPanel } from "./assistantPanelContext";
import type { PanelPlacement } from "./panelPlacement";
import { panelWidthPreference } from "./panelPreferences";


const cssWidth = (placement: PanelPlacement) => (placement.kind === "fullscreen" ? "100%" : `${placement.widthPx}px`);

// A non-modal slide-over: no overlay, no focus trap, page stays fully interactive while it's open.
// zIndex sits at Chakra's "overlay" layer (below "modal") so a real modal — e.g. ConversationSwitcher's
// delete-confirmation dialog, rendered inside this panel — still stacks above it.
export const AssistantPanel = ({
  placement,
  widthSwitchApplies,
}: {
  placement: PanelPlacement | null;
  widthSwitchApplies: boolean;
}) => {
  const { t } = useTranslation();
  const { panelOpen, closePanel } = useAssistantPanel();
  const { pathname } = useLocation();
  const personId = useLoggedInWithOrgContextState().me.id;
  const [choice, setChoice] = panelWidthPreference.use(personId);
  const onAssistantPage = pathname === urls.app.assistant;
  const lastPlacement = useRef<PanelPlacement>({ kind: "over", widthPx: 400 });
  if (placement) lastPlacement.current = placement;
  const shown = lastPlacement.current;

  useEffect(() => {
    if (onAssistantPage) {
      closePanel();
    }
  }, [onAssistantPage, closePanel]);


  if (onAssistantPage) {
    return null;
  }

  const widthLabel = choice === "wide" ? t("Narrower panel") : t("Wider panel");

  return (
    <Slide
      direction="right"
      in={panelOpen}
      unmountOnExit
      style={{ zIndex: "var(--chakra-zIndices-overlay)", width: cssWidth(shown), transition: "width 0.2s ease" }}
    >
      <Box
        h="full"
        display="flex"
        flexDirection="column"
        overflow="hidden"
        bg="smBackground"
        borderLeftWidth="1px"
        borderColor="smBorder"
        boxShadow={shown.kind === "beside" ? "none" : "lg"}
        position="relative"
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            closePanel();
          }
        }}
      >
        {widthSwitchApplies && (
          <Tooltip label={widthLabel} placement="bottom">
            <IconButton
              aria-label={widthLabel}
              icon={<Icon as={choice === "wide" ? FiChevronsRight : FiChevronsLeft} />}
              size="sm"
              variant="ghost"
              color="smMuted"
              position="absolute"
              top="2"
              insetEnd="12"
              onClick={() => setChoice(choice === "wide" ? "compact" : "wide")}
            />
          </Tooltip>
        )}
        <Tooltip label={t("Close the assistant")} placement="bottom">
          <CloseButton
            aria-label={t("Close the assistant") ?? undefined}
            onClick={closePanel}
            position="absolute"
            top="2"
            insetEnd="3"
          />
        </Tooltip>
        <WorkingOnLabel position="absolute" top="3" insetStart="6" insetEnd={shown.kind === "fullscreen" ? "14" : "24"} />
        <Box flex="1" display="flex" flexDirection="column" overflow="hidden" pt="10" pb="4" px="6">
          <AssistantChat />
        </Box>
      </Box>
    </Slide>
  );
};
